import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { COLLECTION } from "../../../../data/constant";

const RANGE_OPTIONS = [7, 15, 30, 90, 180];

const cardStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 150,
  fontFamily: "'Montserrat', sans-serif",
};

/* ---------------- DATE HELPERS ---------------- */
const formatDate = (date) => date.toISOString().split("T")[0];
const parseTimestamp = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === "number") return new Date(ts * 1000);
  if (ts instanceof Date) return ts;
  return new Date(ts);
};

/* ---------------- USERS TAB ---------------- */
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [walletDeductions, setWalletDeductions] = useState([]);
  const [userRange, setUserRange] = useState(30);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      const userSnap = await getDocs(collection(db, COLLECTION.USER));
      setUsers(userSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const walletSnap = await getDocs(collection(db, COLLECTION.WALLET_DEDUCTIONS));
      setWalletDeductions(walletSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchData();
  }, []);

  /* ---------------- DATE RANGE ---------------- */
  const getDateRange = () => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - userRange + 1);
    startDate.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);
    return { startDate, today };
  };

  /* ---------------- DAILY COUNTS ---------------- */
  const generateDailyCounts = (data, timestampKey) => {
    const { startDate, today } = getDateRange();
    const counts = {};
    for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
      counts[formatDate(new Date(d))] = 0;
    }
    data.forEach((item) => {
      const date = parseTimestamp(item[timestampKey]);
      if (!date) return;
      if (date >= startDate && date <= today) {
        const key = formatDate(date);
        if (!(key in counts)) counts[key] = 0;
        counts[key] += 1;
      }
    });
    return Object.entries(counts).map(([x, y]) => ({ x, y }));
  };

  /* ---------------- USER MAP ---------------- */
  const userMap = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      if (u.phoneNumber) map[String(u.phoneNumber)] = u.userName || "Unknown";
    });
    return map;
  }, [users]);

  /* ---------------- KPI CALCULATIONS ---------------- */
  const totalUsers = users.length;

  const activeUserPhones = useMemo(() => {
    const set = new Set();
    walletDeductions.forEach((wd) => {
      const phone = String(wd.phoneNumber || "").trim();
      if (phone && Number(wd.amountDeducted) > 0) {
        set.add(phone);
      }
    });
    return set;
  }, [walletDeductions]);

  const activeUsers = useMemo(() => {
    let count = 0;
    users.forEach((u) => {
      const phone = String(u.phoneNumber || "").trim();
      if (phone && activeUserPhones.has(phone)) count += 1;
    });
    return count;
  }, [users, activeUserPhones]);

  const avgSpendingPerActiveUser = useMemo(() => {
    if (activeUsers === 0) return 0;
    let totalSpent = 0;
    walletDeductions.forEach((wd) => {
      const phone = String(wd.phoneNumber || "").trim();
      if (activeUserPhones.has(phone)) totalSpent += Number(wd.amountDeducted || 0);
    });
    return totalSpent / activeUsers;
  }, [walletDeductions, activeUsers, activeUserPhones]);

  const negativeUsers = users.filter((u) => u.walletAmount < 0).length;
  const totalValidBalance = users
    .filter((u) => u.walletAmount > 0)
    .reduce((a, u) => a + (u.walletAmount || 0), 0);
  const negativeBalanceTotal = users
    .filter((u) => u.walletAmount < 0)
    .reduce((a, u) => a + (u.walletAmount || 0), 0);

  /* ---------------- NEW USERS PER DAY ---------------- */
  const newUsersData = useMemo(
    () => generateDailyCounts(users, "createdTimestamp"),
    [users, userRange]
  );

  /* ---------------- ACTIVE USERS PER DAY ---------------- */
  const activeUsersData = useMemo(() => {
    const { startDate, today } = getDateRange();
    const map = {};
    for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
      map[formatDate(new Date(d))] = new Set();
    }
    walletDeductions.forEach((wd) => {
      const date = parseTimestamp(wd.Timestamp);
      if (!date) return;
      if (date >= startDate && date <= today) {
        const key = formatDate(date);
        if (!map[key]) map[key] = new Set();
        const phone = String(wd.phoneNumber || "").trim();
        if (phone) map[key].add(phone);
      }
    });
    return Object.entries(map).map(([x, set]) => ({ x, y: set.size }));
  }, [walletDeductions, userRange]);

  /* ---------------- ACTIVE USERS DISTRIBUTION CURVE ---------------- */
  const distributionData = useMemo(() => {
    const deductionCountMap = {};
    users.forEach((u) => {
      const phone = String(u.phoneNumber || "").trim();
      if (!activeUserPhones.has(phone)) return;
      deductionCountMap[phone] = 0;
    });

    walletDeductions.forEach((wd) => {
      const phone = String(wd.phoneNumber || "").trim();
      if (deductionCountMap[phone] !== undefined) {
        deductionCountMap[phone] += 1;
      }
    });

    const counts = Object.values(deductionCountMap);
    if (!counts.length) return [];

    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    const stdDev = Math.sqrt(
      counts.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / counts.length
    );

    const buckets = [
      { label: "1–9", min: 1, max: 9 },
      { label: "10–19", min: 10, max: 19 },
      { label: "20–29", min: 20, max: 29 },
      { label: "30–49", min: 30, max: 49 },
      { label: "50+", min: 50, max: Infinity },
    ];

    return buckets.map((b) => {
      const userCount = counts.filter((c) => c >= b.min && c <= b.max).length;
      const xMid = b.max === Infinity ? b.min + 20 : (b.min + b.max) / 2;

      const normalCurveValue =
        (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
        Math.exp(-0.5 * Math.pow((xMid - mean) / stdDev, 2)) *
        counts.length *
        10;

      return {
        range: b.label,
        users: userCount,
        curve: Number(normalCurveValue.toFixed(2)),
      };
    });
  }, [users, walletDeductions, activeUserPhones]);

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ mt: 2, fontFamily: "'Montserrat', sans-serif" }}>
      {/* ---------------- KPI CARDS ---------------- */}
      <Grid container spacing={2} justifyContent="space-between">
        {[
          ["Total Users", totalUsers],
          ["Active Users", activeUsers],
          ["Total Valid Balance", `₹${totalValidBalance}`],
          ["Negative Users", negativeUsers],
          ["Negative Balance", `₹${negativeBalanceTotal}`],
          ["Avg Spend / Active User", `₹${avgSpendingPerActiveUser.toFixed(2)}`],
        ].map(([label, value]) => (
          <Grid item xs="auto" key={label}>
            <Paper sx={cardStyle}>
              <Typography variant="subtitle2" color="textSecondary">{label}</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ---------------- GRAPHS ---------------- */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* New Users */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">New Users per Day</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={userRange} onChange={(e) => setUserRange(+e.target.value)}>
                  {RANGE_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={newUsersData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Line dataKey="y" stroke="#1976d2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Active Users */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">Active Users per Day</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={userRange} onChange={(e) => setUserRange(+e.target.value)}>
                  {RANGE_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={activeUsersData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="y" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Active Users Distribution Curve (replacing Top Spending Users) */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 350 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Active Users Distribution
            </Typography>
            <Typography variant="caption" color="textSecondary">
              X-Axis: Number of transaction per active user<br />
              Y-Axis: Number of active users in that range
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={distributionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="users" fill="#8884d8" />
                <Line
                  type="monotone"
                  dataKey="curve"
                  stroke="#1976d2"
                  strokeWidth={3}
                  dot={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsersTab;
